import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// ---- Path normalization (GDPR: no personal IDs stored) ----
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const HEX_ID_PATTERN = /\/[a-f0-9]{20,}/gi;

function normalizePath(path: string): string {
  let normalized = path.split("?")[0].split("#")[0];

  // Replace UUIDs
  normalized = normalized.replace(UUID_PATTERN, ":uuid");

  // Replace hex IDs
  normalized = normalized.replace(HEX_ID_PATTERN, "/:id");

  // Specific route patterns
  normalized = normalized.replace(/\/auction\/[^/]+/g, "/auction/:id");
  normalized = normalized.replace(/\/player\/[^/]+/g, "/player/:uuid");
  normalized = normalized.replace(/\/market\/[^/]+/g, "/market/:material");
  normalized = normalized.replace(/\/docs\/[^/]+/g, "/docs/:docId");
  normalized = normalized.replace(/\/dev\/[^/]+/g, "/dev/:toolId");

  return normalized || "/";
}

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getOrCreateSessionId(): string {
  const KEY = "anon_session_id";
  let sid = sessionStorage.getItem(KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(KEY, sid);
  }
  return sid;
}

interface BufferedEvent {
  type: "page_view" | "heartbeat";
  path?: string;
  device?: string;
  session_id: string;
  duration_seconds?: number;
}

const FLUSH_INTERVAL = 30_000; // 30 seconds
const HEARTBEAT_INTERVAL = 60_000; // 60 seconds

/**
 * GDPR-compliant analytics tracker.
 * - No user IDs stored
 * - Random session_id in sessionStorage (dies with tab)
 * - Paths normalized to remove personal identifiers
 * - No cookies, no fingerprinting
 */
export function useAnalytics() {
  const location = useLocation();
  const bufferRef = useRef<BufferedEvent[]>([]);
  const sessionIdRef = useRef<string>("");
  const deviceRef = useRef<string>("desktop");
  const mountTimeRef = useRef<number>(Date.now());

  // Flush buffer to edge function
  const flush = useCallback((events?: BufferedEvent[]) => {
    const toSend = events || bufferRef.current.splice(0);
    if (toSend.length === 0) return;

    const payload = JSON.stringify({ events: toSend });
    const url = `https://db.opauction.net/functions/v1/analytics-collect`;

    // Use sendBeacon for reliability (works during page unload)
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      const sent = navigator.sendBeacon(url, blob);
      if (!sent) {
        // Fallback to fetch
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  }, []);

  // Initialize
  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
    deviceRef.current = getDeviceType();
    mountTimeRef.current = Date.now();
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!sessionIdRef.current) return;

    const normalizedPath = normalizePath(location.pathname);

    bufferRef.current.push({
      type: "page_view",
      path: normalizedPath,
      device: deviceRef.current,
      session_id: sessionIdRef.current,
    });
  }, [location.pathname]);

  // Periodic flush + heartbeat
  useEffect(() => {
    // Flush buffer every 30 seconds
    const flushTimer = setInterval(() => {
      flush();
    }, FLUSH_INTERVAL);

    // Heartbeat every 60 seconds
    const heartbeatTimer = setInterval(() => {
      if (!sessionIdRef.current) return;
      const elapsed = Math.round((Date.now() - mountTimeRef.current) / 1000);

      bufferRef.current.push({
        type: "heartbeat",
        session_id: sessionIdRef.current,
        duration_seconds: elapsed,
      });
    }, HEARTBEAT_INTERVAL);

    // Flush on page unload
    const handleUnload = () => {
      flush(bufferRef.current);
    };

    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flush(bufferRef.current.splice(0));
      }
    });

    return () => {
      clearInterval(flushTimer);
      clearInterval(heartbeatTimer);
      window.removeEventListener("beforeunload", handleUnload);
      // Final flush on unmount
      flush();
    };
  }, [flush]);
}
